/**
 * Wraps label + control + hint / error / success; links `htmlFor` to control `id`.
 *
 * When `children` is a single React element, `cloneElement` assigns `id` (from `controlId` or auto-generated),
 * `aria-describedby`, `aria-invalid`, etc. Valid controls: `Input`, `Select`, `Textarea`, …
 */
import React, { useId, useMemo } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '../cn';
const GAP = 'var(--form-field-gap, 6px)';
export const FormField = ({ label, required = false, hint, errorMessage, successMessage, metaRight, state = 'default', controlId, children, className, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const rid = useId();
    const resolvedId = controlId !== null && controlId !== void 0 ? controlId : `ff-${rid}`;
    const hintId = `${resolvedId}-hint`;
    const errorId = `${resolvedId}-error`;
    const successId = `${resolvedId}-success`;
    const showError = state === 'error' && !!errorMessage;
    const showSuccess = state === 'success' && !!successMessage;
    const showHint = !showError && !showSuccess && !!hint;
    const describedBy = useMemo(() => {
        const ids = [];
        if (showError)
            ids.push(errorId);
        else if (showSuccess)
            ids.push(successId);
        else if (showHint)
            ids.push(hintId);
        return ids.join(' ') || undefined;
    }, [errorId, hintId, showError, showHint, showSuccess, successId]);
    const control = React.isValidElement(children)
        ? React.cloneElement(children, {
            id: (_b = (_a = children.props) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : resolvedId,
            'aria-describedby': (_d = (_c = children.props) === null || _c === void 0 ? void 0 : _c['aria-describedby']) !== null && _d !== void 0 ? _d : describedBy,
            'aria-invalid': (_f = (_e = children.props) === null || _e === void 0 ? void 0 : _e['aria-invalid']) !== null && _f !== void 0 ? _f : (showError ? true : undefined),
            'aria-required': (_h = (_g = children.props) === null || _g === void 0 ? void 0 : _g['aria-required']) !== null && _h !== void 0 ? _h : (required ? true : undefined),
            'aria-disabled': (_k = (_j = children.props) === null || _j === void 0 ? void 0 : _j['aria-disabled']) !== null && _k !== void 0 ? _k : (state === 'disabled' ? true : undefined),
        })
        : children;
    return (React.createElement("div", { className: cn('w-full', className), style: { display: 'grid', gap: GAP } },
        React.createElement("div", { className: "flex items-end justify-between gap-2" },
            React.createElement("label", { htmlFor: resolvedId, className: "min-w-0 text-[length:var(--text-body,14px)] font-medium text-[color:var(--form-fg,#0b1220)]" },
                React.createElement("span", { className: "truncate" }, label),
                required ? (React.createElement("span", { className: "ml-1 align-top text-[length:var(--text-meta,11px)] text-[color:var(--form-required,#dc2626)]", "aria-hidden": "true" }, "*")) : null),
            metaRight ? (React.createElement("div", { className: "shrink-0 text-[length:var(--text-hint,12px)] text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]" }, metaRight)) : null),
        React.createElement("div", null, control),
        showError ? (React.createElement("div", { id: errorId, className: "flex items-start gap-1.5 text-[length:var(--text-hint,12px)] text-[color:var(--form-border-error,#dc2626)]", role: "alert" },
            React.createElement(AlertCircle, { className: "mt-0.5 h-4 w-4", "aria-hidden": true }),
            React.createElement("div", { className: "min-w-0" }, errorMessage))) : showSuccess ? (React.createElement("div", { id: successId, className: "flex items-start gap-1.5 text-[length:var(--text-hint,12px)] text-[color:var(--form-border-success,#16a34a)]" },
            React.createElement(CheckCircle2, { className: "mt-0.5 h-4 w-4", "aria-hidden": true }),
            React.createElement("div", { className: "min-w-0" }, successMessage))) : showHint ? (React.createElement("div", { id: hintId, className: "text-[length:var(--text-hint,12px)] text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]" }, hint)) : null));
};
export default FormField;
